// ----------------------------------------------------------------------
// <copyright file="Izenda.ProcessDataUtils.js" company="Izenda">
//  Copyright (c) 2015 Izenda, Inc.
//  ALL RIGHTS RESERVED
//
//  The entire contents of this file is protected by U.S. and
//  International Copyright Laws. Unauthorized reproduction,
//  reverse-engineering, and distribution of all or any portion of
//  the code contained in this file is strictly prohibited and may
//  result in severe civil and criminal penalties and will be
//  prosecuted to the maximum extent possible under the law.
//
//  RESTRICTIONS
//
//  THIS SOURCE CODE AND ALL RESULTING INTERMEDIATE FILES
//  ARE CONFIDENTIAL AND PROPRIETARY TRADE
//  SECRETS OF IZENDA INC. THE REGISTERED DEVELOPER IS
//  LICENSED TO DISTRIBUTE THE PRODUCT AND ALL ACCOMPANYING .NET
//  CONTROLS AS PART OF AN EXECUTABLE PROGRAM ONLY.
//
//  THE SOURCE CODE CONTAINED WITHIN THIS FILE AND ALL RELATED
//  FILES OR ANY PORTION OF ITS CONTENTS SHALL AT NO TIME BE
//  COPIED, TRANSFERRED, SOLD, DISTRIBUTED, OR OTHERWISE MADE
//  AVAILABLE TO OTHER INDIVIDUALS WITHOUT EXPRESS WRITTEN CONSENT
//  AND PERMISSION FROM IZENDA INC.
//
//  CONSULT THE END USER LICENSE AGREEMENT(EULA FOR INFORMATION ON
//  ADDITIONAL RESTRICTIONS.
//  </copyright>
// ----------------------------------------------------------------------
!(function() {
  var RANGE_ALIAS = require('logic/data/Izenda.ReportPartChartConstants').RANGE_ALIAS;
  var AppGlobal = require('Izenda.AppGlobal');
  var SORT_ORDERS = require('logic/data/Izenda.ReportPartFieldConstants').SORT_ORDERS;
  var ReportPartUtils = require('share/utils/Izenda.ReportPartUtils');
  const {
    head,
    tail,
    orderBy,
    toLower,
    map,
    mapValues,
    isUndefined,
    curry,
    concat,
    flatten,
    toNumber,
    isNull,
    find,
    isNil,
    sortBy,
    flow,
    compact,
    clone,
    forEach,
    union,
    isEqual,
    flatMap,
    uniqWith,
    get,
    getOr,
    chain
  } = require('lodash/fp');
  /**
   * group by data using array of fields
   * @param {array of object} data       original data
   * @param {array of string} arrayField list of field need to group by
   * @param {number} level current recursive group level
   * @param {boolean} shouldSortData should sort BE data
   * @return array of field value and data in format :
   * [field1.value1,{data group by value1}, field1.value2, {data group by value 2}]
   */
  function groupByRecursive(data, arrayField, level, shouldSortData, forDrilldown) {
    var newLevel = level || 1;
    var topField = head(arrayField);
    var remainFields = tail(arrayField);

    if (!topField) {
      return [];
    }

    // Should not order data as BE has already sort before and FE using different sort algorithm compare to BE
    var sortOrder = topField.element.properties.getDataValueByKey('sort');
    var getdata = function(pair) {
      var key = pair[0];

      // Use '' to make sure null value make to the top
      return isNull(key) ? '' : !isNaN(key) ? toNumber(key) : key;
    };

    var orderFn = null;

    if (shouldSortData && (sortOrder === SORT_ORDERS.ASC.value || sortOrder === SORT_ORDERS.DESC.value)) {
      orderFn = orderBy([getdata], [toLower(sortOrder)]);
    } else {
      // Do nothing, return data for the map
      orderFn = function(unsortData) {
        return unsortData;
      };
    }

    var convertDataToString = function(splitData) {
      return map(
        mapValues(function(value) {
          return String.commonStringValue(value, AppGlobal.advancedSettings.other.convertNullToEmptyStringValue);
        }),
        splitData
      );
    };

    const groupByAndToPairs = function(columnName, records) {
      var result = [];

      var recordsArray = records.data || records;
      // Group by and to pair
      recordsArray.forEach(function(record) {
        var value = isUndefined(record[columnName]) ? 'undefined' : record[columnName];
        var existingGroup = null;
        if(!forDrilldown || remainFields.length > 0){
          existingGroup = find(function(group) {
            return group[0] === value;
          }, result);
        }
        if (existingGroup) {
          existingGroup[1].push(record);
        } else {
          result.push([value, [record]]);
        }
      });

      return result;
    };

    var flowData = flow(
      curry(groupByAndToPairs)(topField.columnName),
      orderFn,
      map(function(item) {
        var splitData = item[1];
        var key = {
          level: newLevel,
          columnName: topField.columnName,
          element: topField.element,
          value: String.commonStringValue(item[0], AppGlobal.advancedSettings.other.convertNullToEmptyStringValue)
        };
        if (remainFields.length > 0) {
          return {
            key: [key],
            data: convertDataToString(splitData),
            subGroup: groupByRecursive(splitData, remainFields, newLevel + 1, shouldSortData, forDrilldown)
          };
        }
        return {
          key: [key],
          data: convertDataToString(splitData)
        };
      })
    );

    return flowData(data);
  }

  /**
   * flatten data from multi level into single level
   * @param  {Array of JSON} data          Data had been grouped into multi level
   * @param  {Array of JSON} levelKeyArray List of Key from upper level
   * @return {Array of JSON}               List of data had been flatten
   */
  function flatenGroupByRecursive(data, levelKeyArray) {
    var newLevelKeyArray = levelKeyArray || [];
    var subLevelKeyArray = [];
    var flowData = flow(
      map(function(item) {
        var key = item.key;
        ensure(key, 'key must exists');
        subLevelKeyArray = concat(newLevelKeyArray, key);

        if (item.subGroup) {
          return flatenGroupByRecursive(item.subGroup, subLevelKeyArray);
        }
        return {
          key: subLevelKeyArray,
          data: item.data || []
        };
      }),
      flatten
    );
    return flowData(data);
  }

  /**
   * split data into grouped data by using separator
   * @param  {Array of JSON} data       raw data from API
   * @param  {Array of mapping} separator  each object have fieldNameAlias and fieldMappingName
   * @param  {boolean} multiLevel break data into multi level structure or nextPosition
   * @param  {boolean} shouldSortData should sort data
   * @return {Array of JSON}            data had been group
   */
  function splitDataForSeparator(data, separator, multiLevel, shouldSortData, forDrilldown) {
    var firstLevel = 1;
    var groupedByData = groupByRecursive(data, separator, firstLevel, shouldSortData, forDrilldown);
    if (multiLevel) {
      return groupedByData;
    }
    return flatenGroupByRecursive(groupedByData);
  }

  function filterMappingByContainer(fieldsMapping, container) {
    const containerElements = isNil(container) ? [] : sortBy('position', container.elements);

    return flow(
      map(item => {
        const field = item.field || {};
        let newMappingItem = clone(
          find(
            {
              fieldNameAlias: field.fieldNameAlias,
              fieldId: field.fieldId
            },
            fieldsMapping
          )
        );

        if (newMappingItem) {
          newMappingItem.element = item;
        }
        return newMappingItem;
      }),
      compact
    )(containerElements);
  }

  function restructureField(data, fieldMappings) {
    var result = {};
    var keys = Object.keys(data);

    keys.forEach(function(key) {
      var elements = data[key];

      var fieldByKey = [];
      elements.map(function(element) {
        var matchingField = fieldMappings.filter(function(field) {
          return (
            field.fieldNameAlias === RANGE_ALIAS.MIN_ALIAS + element.name ||
            field.fieldNameAlias === RANGE_ALIAS.MAX_ALIAS + element.name ||
            field.fieldNameAlias === RANGE_ALIAS.AVG_ALIAS + element.name ||
            field.fieldNameAlias === element.name
          );
        });

        // link to report-part-element
        forEach(function(field) {
          field.reportPartElm = element;
        }, matchingField);

        fieldByKey = union(matchingField, fieldByKey);
      });

      result[key] = uniqWith(isEqual, fieldByKey);
    });

    return result;
  }

  /**
   * Get list value by field mapping
   * @param {Object} fieldMapping
   * @param {Array} listRecords
   * @param {Object} fieldConfig
   * @returns {Array} list value look up form listRecords
   */
  function getListValueByField(fieldMapping, listRecords, fieldConfig = {}) {
    return flatMap((record) => {
      const originalValue = ReportPartUtils.getOriginalValue(fieldMapping, record);
      const alternativeText = ReportPartUtils.getAlternativeText(originalValue, fieldConfig, record);
      return alternativeText || originalValue;
    }, listRecords);

  }

  /**
   * Get list value by field mapping
   * @param {Object} fieldMapping
   * @param {Array} listRecords
   * @returns {Array} list value look up form listRecords
   */
  function getListValueByGroup(label, fieldMapping, listRecords) {
    var columnName = fieldMapping.columnName;
    var listValues = listRecords.filter(function(record) {
      return !!record[columnName];
    });

    return flatMap(function(value) {
      return {
        key: value[label.columnName],
        value: value[columnName]
      };
    }, listValues);
  }

  /**
   * Get data for field
   * @param  {object} field         data source field
   * @param  {[type]} fieldsMapping field (returned from calling API (fusion))
   * @param  {[type]} record        record (returned from calling API (fusion))
   * @return {any}
   */
  function getValueOfField(field, fieldsMapping, record) {
    if (!field) {
      return null;
    }

    var fieldContainers = {
      field: [field]
    };

    var restructuredField = restructureField(fieldContainers, fieldsMapping);
    restructuredField = getOr({}, 'field[0]', restructuredField);

    return record[restructuredField.columnName];
  }

  function getValueFromMappingField(fieldMapping, records) {
    var columnName = fieldMapping.columnName;

    if (!columnName) {
      return null;
    }

    if (records.length) {
      return map(columnName, records);
    }

    return records[fieldMapping.columnName];
  }

  /**
   * get serie config
   * @param  {Object} field
   * @param  {Object} seriesConfig
   * @return {Object}
   */

  function getFieldName(field) {
    return get('fieldNameAlias', field);
  }

  /**
   * Get minimum and maximum range for color settings
   * @param colorSettings field color settings
   * @param fieldsMapping fields mapping
   * @param record record
   * @returns {null|{isDynamic: boolean, min: number, max: number}}
   */
  function getMinMaxFieldRange(colorSettings, fieldsMapping, record) {
    const colorSettingsValue = getOr(null, 'value', colorSettings);
    if (!colorSettingsValue || !colorSettingsValue.length)
      return null;

    const getFieldValue = (field, fieldsMapping, rec) => {
      if (field) {
        const fieldMapping = find(x => x.fieldNameAlias === field.fieldName, fieldsMapping);
        if (fieldMapping)
          return getOr(null, fieldMapping.columnName, rec);
      }
      return null;
    };

    let isDynamic = false;
    const minMax = chain(colorSettingsValue)
      .sortBy(v => (v.name === 'High' ? 1 : 0))
      .map(v => {
        if (v.thresholdType === 'static') {
          // Use static value as threshold
          return v.key;
        } else if (v.thresholdType === 'dynamic_subtotal') {
          // Use subtotal record as threshold
          const field = getOr(null, 'fields[0]', v);
          const fieldName = field.fieldName;
          return record[`subtotal_${fieldName}`];
        } else {
          // Use custom field+function as threshold
          const field = getOr(null, 'fields[0]', v);
          isDynamic = true;
          return getFieldValue(field, fieldsMapping, record);
        }
      })
      .value();
    return {
      isDynamic: isDynamic,
      min: minMax[0],
      max: minMax[1]
    }
  }

  /**
   * Get "value" type color settings (high + low)
   * @param {any} colorSettings color settings object:
   * {
   *  rangePercent: {...},
   *  rangeValue: {},
   *  value: [{
   *    key: 0, // static value
   *    thresholdType: '', // 'static'|'dynamic'
   *    fields: [], // list of selected field models (SettingsByRangeItemFieldModel)
   *  }]
   * }
   * @param {any[]} fieldsMapping mapping for fieldsId -> columnName
   * @param {any} record current data record
   * @param {number} pointVal current point
   * @return {any} min/max values.
   */
  function getColorSettingsRange(colorSettings, fieldsMapping, record, pointVal): any {
    // get minimum and maximum values:
    const minMax = getMinMaxFieldRange(colorSettings, fieldsMapping, record);
    if (!minMax)
      return null;

    // process empty values
    let min = minMax.min;
    let max = minMax.max;
    const isRangeInvalid = isNil(min) || isNil(max) || isNaN(min) || isNaN(max);
    if (isRangeInvalid)
      return null;

    // values not empty:
    const range = max - min;
    if (!minMax.isDynamic && range > 1) {
      min = Math.floor(min);
      max = Math.ceil(max);
    }

    return {
      type: 'value',
      range: [[min, max]]
    };
  }

  module.exports = {
    splitDataForSeparator: splitDataForSeparator,
    filterMappingByContainer: filterMappingByContainer,
    getListValueByGroup: getListValueByGroup,
    restructureField: restructureField,
    getListValueByField: getListValueByField,
    getValueOfField: getValueOfField,
    getValueFromMappingField: getValueFromMappingField,
    getFieldName: getFieldName,
    getColorSettingsRange: getColorSettingsRange
  };
})();



// WEBPACK FOOTER //
// ./ui/share/utils/Izenda.ProcessDataUtils.js